import React, { Suspense, useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Html, Environment, useGLTF, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { useAudio } from '../contexts/AudioContext';

import namesData from '../data/every1final.json';

const normalizeChar = (char) => {
  return char.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
};

const groupNamesByFirstLetter = (names) => {
  return names.reduce((acc, name, index) => {
    // Überspringe leere Namen oder Namen die nur aus Whitespace bestehen
    if (!name || name.trim() === '') return acc;
    
    const firstChar = name.charAt(0);
    const normalizedFirstLetter = normalizeChar(firstChar);
    
    if (!acc[normalizedFirstLetter]) {
      acc[normalizedFirstLetter] = [];
    }
    acc[normalizedFirstLetter].push(index);
    return acc;
  }, {});
};

function ScrollableList({ onIndexChange, activeIndex: controlledActiveIndex, setActiveIndex }) {
  const scrollRef = useRef(null);
  const prevActiveIndex = useRef(controlledActiveIndex);
  const [scrollOffsets, setScrollOffsets] = useState({});
  const [isWide, setIsWide] = useState([]); 
  const [missingWidth, setMissingWidth] = useState([]); 

  // Add this at the top of your ScrollableList component
  const [isScrolling, setIsScrolling] = useState(false);


  // Handle manual scrolling
  useLayoutEffect(() => {
    const scrollElement = scrollRef.current;
    const handleScroll = () => {
      if (!scrollElement || isScrolling) return;
    
      const itemHeight = 18;
      const containerHeight = scrollElement.clientHeight;
      const scrollTop = scrollElement.scrollTop;
      
      const centerViewportPosition = scrollTop + (containerHeight / 2);
      const listStartPosition = 3 * itemHeight;
      
      const relativePosition = centerViewportPosition - listStartPosition;
      const middleIndex = Math.round(relativePosition / itemHeight) - 1; // Hier subtrahieren wir 1
      
      if (
        middleIndex !== prevActiveIndex.current && 
        middleIndex >= 0 && 
        middleIndex < namesData.names.length
      ) {
        setActiveIndex(middleIndex);
        onIndexChange?.();
        prevActiveIndex.current = middleIndex;
      }
    };
      
    scrollElement.addEventListener("scroll", handleScroll);
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [onIndexChange, setActiveIndex, isScrolling]);

  // Handle programmatic scrolling when activeIndex changes
  useEffect(() => {
    if (scrollRef.current && controlledActiveIndex !== prevActiveIndex.current) {
      // Constants
      const itemHeight = 18;
      const containerHeight = scrollRef.current.clientHeight;
      const headerHeight = 3 * itemHeight; // Height of the header items
      
      // Set flag to prevent scroll handler from interfering
      setIsScrolling(true);
      
      // Calculate the absolute position of the item we want to center
      const itemAbsolutePosition = headerHeight + (controlledActiveIndex * itemHeight);
      
      // Calculate the position that would center this item in the viewport
      // We want the middle of the item to be at the middle of the viewport
      const targetScrollTop = itemAbsolutePosition - (containerHeight / 2) + (itemHeight / 2);
      
      // Set the scroll position
      scrollRef.current.scrollTop = targetScrollTop;
      prevActiveIndex.current = controlledActiveIndex;
      
      // Release the scroll lock after a short delay to ensure the scroll has completed
      setTimeout(() => {
        setIsScrolling(false);
      }, 50);
    }
  }, [controlledActiveIndex]);
  
  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      const widths = namesData.names.map((name, i) => {
        const span = document.getElementById(`name-${i}`);
        const li = document.getElementById(`li-${i}`);
        if (span && li) {
          const spanWidth = span.getBoundingClientRect().width;
          const liWidth = li.getBoundingClientRect().width;
          const missing = spanWidth > liWidth ? spanWidth - liWidth : 0;
          return { missing, isWide: spanWidth > liWidth };
        }
        return { missing: 0, isWide: false };
      });
  
      const missingWidths = widths.map(item => item.missing);
      const wideItems = widths.map(item => item.isWide);
      
      setMissingWidth(missingWidths);
      setIsWide(wideItems);
    }, 500);
      
    return () => clearTimeout(timer);
  }, [namesData, controlledActiveIndex]);
    
  return (
    <div
      ref={scrollRef}
      style={{
        width: "190px",
        height: "162px",
        overflowY: "scroll",
        borderRadius: "24px",
        color: "white",
        padding: "10px",
        fontFamily: "Ubuntu",
        fontSize: "13px",
        textTransform: 'uppercase',
        scrollSnapType: "y mandatory",
        scrollPadding: "18px"
      }}
      className={'scrollable'}
    >
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        <li style={{ height: "18px", maxHeight: "18px", minHeight: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>*** 2009 - 2025 ***</li>
        <li style={{ height: "27px", maxHeight: "27px", minHeight: "27px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
        {namesData.names.map((name, i) => {
          const isActive = i === controlledActiveIndex;
          const isWideItem = isWide[i];
          const offset = scrollOffsets[i] || 0;
          const missing = missingWidth[i] || 0;
  
          return (
            <li
              key={i}
              id={`li-${i}`}
              style={{
                color: isActive ? "black" : "white",
                height: "18px",
                maxHeight: "18px",
                minHeight: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                background: isActive ? "rgba(255, 255, 255, 0.9)" : "transparent",
                borderRadius: "4px",
                transition: "background 0.2s, color .2s",
                whiteSpace: "nowrap",
                overflow: "hidden",
                width: "170px",
                scrollSnapAlign: "start",
                scrollSnapStop: "always"
              }}
              className={isActive ? "active" : "non-active"}
            >
              <span
                id={`name-${i}`}
                style={{
                  transform: `translateX(${offset}px)`,
                  transition: isActive ? "transform 0.05s linear" : "none",
                  display: "inline-block",
                  paddingLeft: "10px",
                  "--missing-width": `${missing}px`,
                }}
                className={isWideItem ? "wide-item" : ""}
              >
                {name}
              </span>
            </li>
          );
        })}
        <li style={{ height: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
        <li style={{ height: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
        <li style={{ height: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
        <li style={{ height: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
        <li style={{ height: "18px", scrollSnapAlign: "start", scrollSnapStop: "always"}}>&nbsp;</li>
      </ul>
    </div>
  );
}

function Model(props) {
  const group = useRef();
  const { nodes, materials } = useGLTF("/tamagotchi11.glb");
  const [scale, setScale] = useState(0);
  const mouseOffset = useRef(new THREE.Vector3(0, 0, 0));
  const rotation = useRef(new THREE.Vector3(0, 0, 0));
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovered, setHovered] = useState({
    play: false,
    music: false,
    backwards: false
  });

  const nameGroups = useMemo(() => groupNamesByFirstLetter(namesData.names), []);
  
  const [buttonMaterials] = useState({
    play: materials['Material.001'].clone(),
    music: materials['Material.001'].clone(),
    backwards: materials['Material.001'].clone()
  });
  
  const { playSound, stopSound } = useAudio();

  const handleIndexChange = () => {
    playSound('list');
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      handleMusicClick({ stopPropagation: () => {} });
    }, 1000);
  
    return () => clearTimeout(timer);
  }, []); 
    
  useEffect(() => {
    const baseMaterial = materials['Material.001'];
    const originalColor = baseMaterial.color.clone();
    const originalMap = baseMaterial.map;
    const originalEmissive = baseMaterial.emissive?.clone();
  
    Object.entries(hovered).forEach(([key, isHovered]) => {
      if (buttonMaterials[key]) {
        const material = buttonMaterials[key];
        
        if (isHovered) {
          material.color.set('pink');
          material.map = originalMap;
          material.emissive = originalEmissive;
        } else {
          material.color.copy(originalColor);
          material.map = originalMap;
          material.emissive = originalEmissive;
        }
      }
    });
  }, [hovered, materials, buttonMaterials, isMusicPlaying]);
  
  const previousButtonRef = useRef();
  const handlePreviousClick = (e) => {
    e.stopPropagation();
    const currentLetter = namesData.names[activeIndex].charAt(0).toUpperCase();
    const letters = Object.keys(nameGroups).sort();
    const currentLetterIndex = letters.indexOf(currentLetter);
  
    if (currentLetterIndex < letters.length - 1) {
      const nextLetter = letters[currentLetterIndex + 1];
      const firstIndexForLetter = nameGroups[nextLetter][0];
      setActiveIndex(firstIndexForLetter);
      playSound('button');
      previousButtonRef.current.position.x = -0.08; 
      setTimeout(() => {
        previousButtonRef.current.position.x = 0.0; 
      }, 100)
    }
  };
  
  const musicButtonRef = useRef();
  const handleMusicClick = (e) => {
    e.stopPropagation();
    setIsMusicPlaying(!isMusicPlaying);
    playSound('button');

    if (isMusicPlaying) {
      stopSound('music');
      if (musicButtonRef.current) {
        musicButtonRef.current.position.x = 0.0; 
      }
    } else {
      playSound('music');
      if (musicButtonRef.current) {
        musicButtonRef.current.position.x = -0.08; 
      }
    }
  };
  
  const nextButtonRef = useRef();
  const handleNextClick = (e) => {
    e.stopPropagation();
    const currentLetter = namesData.names[activeIndex].charAt(0).toUpperCase();
    const letters = Object.keys(nameGroups).sort();
    const currentLetterIndex = letters.indexOf(currentLetter);
  
    if (currentLetterIndex > 0) {
      const previousLetter = letters[currentLetterIndex - 1];
      const firstIndexForLetter = nameGroups[previousLetter][0];
      setActiveIndex(firstIndexForLetter);
      playSound('button');
      nextButtonRef.current.position.x = -0.08; 
      setTimeout(() => {
        nextButtonRef.current.position.x = 0.0; 
      }, 100)
    }
  };
    
  const [isAnimating, setIsAnimating] = useState(true);
  const [isAutoAnimating, setIsAutoAnimating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionToAuto, setTransitionToAuto] = useState(false);
  const [hasUserMoved, setHasUserMoved] = useState(false);
  const [showList, setShowList] = useState(false);

  const animationProgress = useRef(0);
  const autoAnimationTime = useRef(0);
  const transitionProgress = useRef(0);
  const lastMousePosition = useRef({ x: 0, y: 0, z: 0 });
  const lastAutoPosition = useRef({ x: 0, y: 0, z: 0 });
  const mouseIdleTimer = useRef(null);
  const initialRotationCompleted = useRef(false);
  const startY = -0.5;

  const animationState = useRef('initial');

  useFrame(() => {
    if (!group.current) return;

     if (isAnimating) {
      if (!initialRotationCompleted.current) {
        animationProgress.current += 0.012;

        const easeInOut = (t) => {
          return t < 0.5
            ? 8 * t * t * t * t
            : 1 - Math.pow(-2 * t + 2, 4) / 2;
        };

        const easedProgress = easeInOut(animationProgress.current);

        // Position und Scale
        const targetPositionY = THREE.MathUtils.lerp(startY, 0, easedProgress);
        group.current.position.y = targetPositionY;
        
        const targetScale = THREE.MathUtils.lerp(0, 1, easedProgress);
        group.current.scale.set(targetScale, targetScale, targetScale);
        setScale(targetScale);

        // Liste früher einblenden
        if (easedProgress >= 0.76 && !showList) {
          setShowList(true);
        }

        // Rotation mit Zustandskontrolle
        if (animationState.current === 'initial') {
          const rotation = Math.min(easedProgress * Math.PI * 2, Math.PI * 2);
          group.current.rotation.y = rotation;

          if (easedProgress >= 0.99) {
            animationState.current = 'completing';
            // Setze exakte finale Rotation
            group.current.rotation.y = Math.PI * 2;
            
            // Speichere finalen Zustand sofort
            group.current.userData.finalState = {
              positionY: group.current.position.y,
              rotationY: Math.PI * 2,
              scale: targetScale
            };

            // Markiere Rotation als abgeschlossen
            initialRotationCompleted.current = true;
            
            // State-Updates in einem Batch
            Promise.resolve().then(() => {
              setIsAnimating(false);
              setIsAutoAnimating(true);
            });
            
            animationProgress.current = 1;
            animationState.current = 'completed';
          }
        }
      }
    } else if (isTransitioning) {
      transitionProgress.current += 0.03;

      const finalState = group.current.userData.finalState;
      
      if (transitionToAuto) {
        const wobble = Math.sin(autoAnimationTime.current * 2) * 0.1;
        const maxRotation = Math.PI / 12;
        const targetRotationY = THREE.MathUtils.clamp(
          finalState.rotationY + wobble,
          finalState.rotationY - maxRotation,
          finalState.rotationY + maxRotation
        );
        
        group.current.rotation.x = THREE.MathUtils.lerp(
          lastMousePosition.current.x,
          0,
          transitionProgress.current
        );
        group.current.rotation.y = THREE.MathUtils.lerp(
          lastMousePosition.current.y,
          targetRotationY,
          transitionProgress.current
        );
        group.current.rotation.z = THREE.MathUtils.lerp(
          lastMousePosition.current.z,
          0,
          transitionProgress.current
        );
      } else {
        const maxRotation = Math.PI / 12;
        const targetRotationX = THREE.MathUtils.clamp(rotation.current.x, -maxRotation, maxRotation);
        const targetRotationY = finalState.rotationY + THREE.MathUtils.clamp(rotation.current.y, -maxRotation, maxRotation);
        const targetRotationZ = THREE.MathUtils.clamp(rotation.current.z, -maxRotation, maxRotation);

        group.current.rotation.x = THREE.MathUtils.lerp(
          lastAutoPosition.current.x,
          targetRotationX,
          transitionProgress.current
        );
        group.current.rotation.y = THREE.MathUtils.lerp(
          lastAutoPosition.current.y,
          targetRotationY,
          transitionProgress.current
        );
        group.current.rotation.z = THREE.MathUtils.lerp(
          lastAutoPosition.current.z,
          targetRotationZ,
          transitionProgress.current
        );
      }

      if (transitionProgress.current >= 1) {
        setIsTransitioning(false);
        setTransitionToAuto(false);
        transitionProgress.current = 0;
        if (transitionToAuto) {
          setIsAutoAnimating(true);
        }
      }
    } else if (isAutoAnimating) {
      // Auto-animation phase
      autoAnimationTime.current += 0.01;
      
      const wobble = Math.sin(autoAnimationTime.current * 2) * 0.1;
      const finalState = group.current.userData.finalState;
      
      const maxRotation = Math.PI / 12;
      const targetRotationY = THREE.MathUtils.clamp(
        finalState.rotationY + wobble,
        finalState.rotationY - maxRotation,
        finalState.rotationY + maxRotation
      );
      
      group.current.rotation.y = targetRotationY;
      group.current.rotation.z = THREE.MathUtils.clamp(
        Math.sin(autoAnimationTime.current * 1.5) * 0.05,
        -maxRotation,
        maxRotation
      );

      lastAutoPosition.current = {
        x: group.current.rotation.x,
        y: group.current.rotation.y,
        z: group.current.rotation.z
      };
    } else {
      // Maus-Animation
      const finalState = group.current.userData.finalState;
      group.current.scale.set(finalState.scale, finalState.scale, finalState.scale);
      group.current.position.y = finalState.positionY;

      const maxRotation = Math.PI / 12;

      const targetRotationX = THREE.MathUtils.clamp(rotation.current.x, -maxRotation, maxRotation);
      const targetRotationY = finalState.rotationY + THREE.MathUtils.clamp(rotation.current.y, -maxRotation, maxRotation);
      const targetRotationZ = THREE.MathUtils.clamp(rotation.current.z, -maxRotation, maxRotation);

      group.current.rotation.x = THREE.MathUtils.lerp(
        group.current.rotation.x, 
        targetRotationX, 
        0.06
      );
      group.current.rotation.y = THREE.MathUtils.lerp(
        group.current.rotation.y, 
        targetRotationY, 
        0.06
      );
      group.current.rotation.z = THREE.MathUtils.lerp(
        group.current.rotation.z, 
        targetRotationZ, 
        0.06
      );

      lastMousePosition.current = {
        x: group.current.rotation.x,
        y: group.current.rotation.y,
        z: group.current.rotation.z
      };
    }
  });


  useEffect(() => {
    // Touch-Device Check
    const isTouchDevice = ('ontouchstart' in window) || 
      (navigator.maxTouchPoints > 0) || 
      (navigator.msMaxTouchPoints > 0);

    if (isTouchDevice) {
      setIsAutoAnimating(true);
      return;
    }

    const handleMouseMove = (event) => {
      if (mouseIdleTimer.current) {
        clearTimeout(mouseIdleTimer.current);
      }

      if (isAutoAnimating) {
        setIsAutoAnimating(false);
        setIsTransitioning(true);
        setTransitionToAuto(false);
        transitionProgress.current = 0;
      }

      if (!hasUserMoved) {
        setHasUserMoved(true);
      }

      const x = (event.clientX / window.innerWidth) * 2 - 1;
      const y = (event.clientY / window.innerHeight) * 2 - 1;

      const maxRotation = Math.PI / 6;
    
      mouseOffset.current.set(
        0,
        y * 0.2,
        Math.abs(y) * 0.3
      );
      
      rotation.current.set(
        0,
        THREE.MathUtils.clamp(x * maxRotation, -maxRotation, maxRotation),
        THREE.MathUtils.clamp(-y * maxRotation, -maxRotation, maxRotation)
      );

      mouseIdleTimer.current = setTimeout(() => {
        setIsTransitioning(true);
        setTransitionToAuto(true);
        transitionProgress.current = 0;
        autoAnimationTime.current = 0;
      }, 2000);
    };
      
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (mouseIdleTimer.current) {
        clearTimeout(mouseIdleTimer.current);
      }
    };
  }, [isAutoAnimating, hasUserMoved]);

  const [listOpacity, setListOpacity] = useState(0);

  // Dann füge einen useEffect hinzu, der die Opazität ändert, wenn die Animation endet
  useEffect(() => {
    if (showList) {
      // Kurze Verzögerung, bevor die Liste eingeblendet wird
      const timer = setTimeout(() => {
        setListOpacity(1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showList]);

  return (
    <group ref={group} {...props} scale={[1, 1, 1]} dispose={null}>
      <mesh 
        geometry={nodes.Tamagochi_shell.geometry} 
        material={materials['Material.001']} 
        rotation={[Math.PI / 2, 0, 0]} 
        scale={0.01}
      />
      <mesh 
        geometry={nodes.lcd_screen.geometry} 
        material={materials['Material.001']} 
        rotation={[Math.PI / 2, 0, 0]} 
        scale={0.01}
      >
      {showList && (
        <Html 
          className="content"
          wrapperClass="html-wrapper"
          position={[20.000, 0, -23.000]}
          rotation={[1.532, 1.542, -3.112]} 
          scale={[18.000, 18.000, 18.000]} 
          transform
          style={{ 
            opacity: listOpacity, 
            transition: 'opacity 0.5s ease-in-out',
            willChange: 'transform, opacity'
          }}
          portal={{
            rootContainer: document.body
          }}
        >
          <ScrollableList 
            onIndexChange={handleIndexChange}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        </Html>
      )}
      </mesh>
      <mesh 
        ref={previousButtonRef}
        geometry={nodes.button_play.geometry} 
        material={buttonMaterials.play}
        rotation={[Math.PI / 2, 0, 0]} 
        scale={0.01}
        onClick={handlePreviousClick}
        onPointerOver={(e) => {
          setHovered(prev => ({ ...prev, play: true }));
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          setHovered(prev => ({ ...prev, play: false }));
          document.body.style.cursor = 'default';
        }}
      />
      <mesh 
        ref={musicButtonRef}
        geometry={nodes.button_music.geometry} 
        material={buttonMaterials.music}
        rotation={[Math.PI / 2, 0, 0]} 
        scale={0.01}
        onClick={handleMusicClick}
        onPointerOver={(e) => {
          setHovered(prev => ({ ...prev, music: true }));
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          setHovered(prev => ({ ...prev, music: false }));
          document.body.style.cursor = 'default';
        }}
      />
      <mesh 
        ref={nextButtonRef}
        geometry={nodes.button_backwards.geometry} 
        material={buttonMaterials.backwards}
        rotation={[Math.PI / 2, 0, 0]} 
        scale={0.01}
        onClick={handleNextClick}
        onPointerOver={(e) => {
          setHovered(prev => ({ ...prev, backwards: true }));
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          setHovered(prev => ({ ...prev, backwards: false }));
          document.body.style.cursor = 'default';
        }}
      />
    </group>
  );
}


function LightingFromTexture() {
  const { scene } = useThree();
  const texture = useTexture("/studio.jpg");
  
  useEffect(() => {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    // Nur die Umgebung setzen, nicht den Hintergrund
    scene.environment = texture;
    return () => { scene.environment = null; };
  }, [scene, texture]);
  
  return null;
}
const Tamagoci = ({ visible, moveBack }) => {

  if (!visible) return null;
  
  return (
    <Canvas
      camera={{ position: [-5, 0, -15], fov: 55 }}
      style={{
        display: 'block',
        width: "900px",
        height: "900px",
        position: "fixed",
        left: "50%",
        top: "calc(50% + 6vh)",
        marginLeft: "-450px",
        marginTop: "-450px",
        transform: "translateZ(0)",
        zIndex: moveBack ? -1 : 10
      }}
      gl={{ 
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 0.8,
        antialias: true,
        pixelRatio: Math.min(window.devicePixelRatio, 2)
      }}
      className={'webgl'}
    >
      <Suspense fallback={null}>
        <group rotation={[0, 1.89, 0]} position={[0, 0, 0]} scale={[5, 5, 5]}>
          <Model />
        </group>
        <ambientLight intensity={0.4} />
        <directionalLight 
          position={[-5, -300, -15]} 
          intensity={0.3} 
          castShadow
        />
        <directionalLight 
          position={[-5, 300, -15]} 
          intensity={0.6} 
          castShadow
        />
        <LightingFromTexture />
      </Suspense>
    </Canvas>
  );
}

export default Tamagoci;